<template>
  <div class="sub-supplier-list">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <label class="font-weight-500 mb-0">Nhà cung cấp phụ:</label>
      <b-button
        v-if="!isViewOnly"
        size="sm"
        variant="outline-primary"
        @click="showModal = true"
      >
        <i class="fa fa-plus mr-1"></i>Thêm NCC phụ
      </b-button>
    </div>

    <b-table
      :items="children"
      :fields="fields"
      :busy="loading"
      show-empty
      empty-text="Chưa có nhà cung cấp phụ nào"
      small
      bordered
      hover
      class="mb-0"
    >
      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner small class="mr-1" />
          <span>Đang tải...</span>
        </div>
      </template>

      <template #cell(status)="{ item }">
        <b-badge :variant="item.status === 1 ? 'success' : 'secondary'">
          {{ item.status === 1 ? 'Đang giao dịch' : 'Ngưng' }}
        </b-badge>
      </template>

      <template #cell(actions)="{ item }">
        <b-dropdown size="sm" no-caret right>
          <template #button-content>
            <i style="font-size: 1rem" class="flaticon2-settings pr-0"></i>
          </template>

          <b-dropdown-item :href="buildDetailUrl(item.id)" target="_blank">
            <span style="color: #3f4254; font-size: 12px">
              <i style="font-size: 1rem" class="fas fa-eye icon-color"></i>
              &nbsp; Xem chi tiết
            </span>
          </b-dropdown-item>

          <template v-if="!isViewOnly">
            <b-dropdown-item @click="confirmDetach(item)">
              <span style="color: #3f4254; font-size: 12px">
                <i style="font-size: 1rem; color: #f39c12" class="fas fa-unlink"></i>
                &nbsp; Gỡ liên kết
              </span>
            </b-dropdown-item>

            <b-dropdown-item @click="confirmDelete(item)">
              <span style="color: #3f4254; font-size: 12px">
                <i style="font-size: 1rem; color: #d33" class="flaticon2-rubbish-bin-delete-button"></i>
                &nbsp; Xóa
              </span>
            </b-dropdown-item>
          </template>
        </b-dropdown>
      </template>
    </b-table>

    <SubSupplierFormModal
      v-if="showModal"
      :parent-id="parentId"
      @created="onCreated"
      @close="showModal = false"
    />
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import ApiService from '@/core/services/api.service';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import SubSupplierFormModal from './SubSupplierFormModal.vue';

export default {
  name: 'SubSupplierList',

  components: { SubSupplierFormModal },

  props: {
    parentId: {
      type: Number,
      required: true,
    },
    isViewOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      children: [],
      loading: false,
      showModal: false,
      fields: [
        {
          key: 'fullName',
          label: 'Tên KH/NCC phụ',
          thStyle: { fontWeight: 600, width: '30%' },
        },
        {
          key: 'phoneNo',
          label: 'Điện thoại',
          thStyle: { fontWeight: 600, width: '15%' },
          formatter: (v) => v || '—',
        },
        {
          key: 'code',
          label: 'Mã nội bộ',
          thStyle: { fontWeight: 600, width: '15%' },
          formatter: (v) => v || '—',
        },
        {
          key: 'taxCode',
          label: 'Mã số thuế',
          thStyle: { fontWeight: 600, width: '15%' },
          formatter: (v) => v || '—',
        },
        {
          key: 'status',
          label: 'Trạng thái',
          thStyle: { fontWeight: 600, width: '15%' },
        },
        {
          key: 'address',
          label: 'Địa chỉ',
          thStyle: { fontWeight: 600, width: '15%' },
        },
        {
          key: 'actions',
          label: '',
          thStyle: { width: '5%' },
          tdClass: 'text-center',
        },
      ],
    };
  },

  mounted() {
    this.fetchChildren();
  },

  methods: {
    buildDetailUrl(id) {
      const route = this.$router.resolve({
        name: 'update-customer',
        query: { id },
      });
      return route.href;
    },

    async fetchChildren() {
      if (!this.parentId) return;
      this.loading = true;
      try {
        const res = await ApiService.query('v1/customers', {
          params: { parentId: this.parentId, size: 100 },
        });
        this.children = res.data?.data?.items || [];
      } catch {
        this.children = [];
      } finally {
        this.loading = false;
      }
    },

    confirmDetach(item) {
      Swal.fire({
        title: 'Gỡ liên kết NCC phụ!',
        text: `Bạn có chắc muốn gỡ liên kết "${item.fullName}" khỏi nhà cung cấp chính không? NCC phụ sẽ không bị xóa.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f39c12',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Gỡ liên kết',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
      }).then((result) => {
        if (result.value) this.doDetach(item);
      });
    },

    confirmDelete(item) {
      Swal.fire({
        title: 'Xóa NCC phụ!',
        text: `Bạn có chắc muốn xóa "${item.fullName}" không? Hành động này không thể hoàn tác.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xóa',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
      }).then((result) => {
        if (result.value) this.doDelete(item);
      });
    },

    async doDetach(item) {
      try {
        await ApiService.put(`v1/customers/${item.id}`, { parentId: null });
        makeToastSuccess(`Đã gỡ liên kết "${item.fullName}"`);
        await this.fetchChildren();
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Có lỗi xảy ra';
        makeToastFaile(msg);
      }
    },

    async doDelete(item) {
      try {
        await ApiService.delete(`v1/customers/${item.id}`);
        makeToastSuccess(`Đã xóa "${item.fullName}"`);
        await this.fetchChildren();
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Có lỗi xảy ra';
        makeToastFaile(msg);
      }
    },

    async onCreated() {
      await this.fetchChildren();
    },
  },
};
</script>

<style scoped>
.sub-supplier-list label {
  font-weight: 500;
}
</style>
